import { isVisibleByPath } from "@components/smart/FieldInfo";
import { ISmartFieldBlur, ISmartFieldChange } from "@components/smart/smartField/SmartField";
import BindingContext, { createPath } from "@odata/BindingContext";
import { getExpandedEnumProps, getNewItemsMaxId } from "@odata/Data.utils";
import {
    EntityBankAccountEntity,
    EntityTypeName,
    IPrCSSZDecisivePeriodEntity,
    IPrEmployeeBankAccountEntity,
    PrCSSZDecisivePeriodEntity,
    PrCSSZSubmissionDetailEntity,
    PrCSSZSubmissionEmployeeEntity,
    PrCSSZSubmissionEmploymentEntity,
    PrEmployeeEntity
} from "@odata/GeneratedEntityTypes";
import { CountryCode, PrCSSZPaymentMethodCode } from "@odata/GeneratedEnums";
import { getBankBlurArgs } from "@pages/banks/bankAccounts/BankAccounts.utils";
import {
    handleIdentificationTypeBeforeSave,
    IdentificationTypeLocalPath,
    initIdentificationTypeLocalPath,
    loadEmployments
} from "@pages/payroll/employee/Employee.utils";
import {
    sortPeriodsAndCreateKey,
    SummaryRowId
} from "@pages/payroll/submission/components/DecisivePeriodsSmartFastEntryList";
import { PrSubmissionGroup } from "@pages/payroll/submission/Submission.def";
import { setCompanyRelation } from "@pages/payroll/submission/Submission.utils";
import { IProps, SubmissionsFormViewForExtend } from "@pages/payroll/submission/SubmissionFormView";
import {
    CSSZSubmissionIncomeType,
    IncomeTypeLocalPath,
    IPrCSSZSubmissionEntity,
    PrCSSZSubmissionEntity
} from "@pages/payroll/submission/submissionTypes/PrCSSZSubmission.utils";
import { handleBankAccountOrIBANBlurWithAccountGeneration } from "@utils/BankUtils";
import { isNotDefined, isObjectEmpty } from "@utils/general";
import { memoizeOne } from "@utils/memoizeOne";

import DateType, { getUtcDayjs, TDateLike } from "../../../../types/Date";
import NumberType from "../../../../types/Number";
import { FormStorage } from "../../../../views/formView/FormStorage";

export default abstract class PrCSSZSubmissionNEMPRIFormView<E extends IPrCSSZSubmissionEntity> extends SubmissionsFormViewForExtend<E> {
    pathToEmployee = createPath(PrCSSZSubmissionEntity.Employee, PrCSSZSubmissionEmployeeEntity.Employee);

    constructor(props: IProps<E>) {
        super(props);

        this.handleLineItemsChange = this.handleLineItemsChange.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
        this.onBeforeSave = this.onBeforeSave.bind(this);
    }

    summaryItem = memoizeOne((): IPrCSSZDecisivePeriodEntity => ({
        Id: SummaryRowId,
        Month: null,
        Year: null,
    }));

    async typeSpecificOnAfterLoad(isNew: boolean): Promise<void> {
        const { storage } = this.props;
        const promises: Promise<void>[] = [];

        if (isNew) {
            promises.push(setCompanyRelation(storage as unknown as FormStorage, { socialFields: true }));
        } else {
            // we need to fetch Employments to know if to show Employment field
            // (it's visible only for 2+ employments)
            promises.push(this.loadEmployments());
        }
        // common for new and existing entity.
        this.initDecisivePeriods(isNew);

        initIdentificationTypeLocalPath(storage,
            storage.data.bindingContext
                .navigate(PrCSSZSubmissionEntity.Employee),
            PrEmployeeEntity.PersonalIdentificationNumber
        );

        promises.push(super.typeSpecificOnAfterLoad(isNew));
        await Promise.all(promises);
    }

    async loadEmployments(): Promise<void> {
        const storage = this.props.storage as unknown as FormStorage;
        const employmentBc = storage.data.bindingContext
            .navigate(PrCSSZSubmissionEntity.Detail)
            .navigate(PrCSSZSubmissionDetailEntity.Employment)
            .navigate(PrCSSZSubmissionEmploymentEntity.Employment);
        return loadEmployments(storage, employmentBc, this.handleChange);
    }

    initDecisivePeriods(isNew?: boolean): void {
        const { storage } = this.props;
        const entity = storage.getEntity();

        const type = storage.getValueByPath(IncomeTypeLocalPath);
        if (!type) {
            // preselect IncomeTypeLocalPath if not set yet, based on existing data
            const newType = entity.Detail?.DecisivePeriods?.length ? CSSZSubmissionIncomeType.DecisivePeriod : CSSZSubmissionIncomeType.EstimatedIncomeAmount;
            storage.setValueByPath(IncomeTypeLocalPath, newType);
        }

        // always update periods. Even new entity could have preloaded data from Event,
        // so function works with current data and keeps them. Generate missing periods if needed.
        this.updateDecisivePeriods();
    }

    updateDecisivePeriods(): void {
        const { storage } = this.props;
        const type = this.props.storage.getValueByPath(IncomeTypeLocalPath);
        let periods: IPrCSSZDecisivePeriodEntity[] = [];
        if (type === CSSZSubmissionIncomeType.DecisivePeriod) {
            const startPath = createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.DateDecisivePeriodStart);
            const endPath = createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.DateDecisivePeriodEnd);
            let from: TDateLike = storage.getValueByPath(startPath);
            let to: TDateLike = storage.getValueByPath(endPath);
            if (isNotDefined(from) || isNotDefined(to) || !DateType.isValid(from) || !DateType.isValid(to)) {
                return;
            }
            if (getUtcDayjs(to).isBefore(from)) {
                storage.setValueByPath(endPath, from);
                storage.setValueByPath(startPath, to);
                from = to;
                to = storage.getValueByPath(endPath);
            }
            // make sure we are in UTC to avoid issues with timezones
            from = getUtcDayjs(from);
            to = getUtcDayjs(to);
            // prepare periods
            const currentPeriods = storage.getValueByPath(createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.DecisivePeriods)) as IPrCSSZDecisivePeriodEntity[];
            let newItemMax = getNewItemsMaxId(currentPeriods);
            while (from.isSameOrBefore(to)) {
                const newPeriod: IPrCSSZDecisivePeriodEntity = {
                    Month: from.month() + 1,
                    Year: from.year(),
                    ExcludedDays: 0,
                    IncludableIncomeAmount: 0
                };
                const currentValue = currentPeriods?.find(p => p.Month === newPeriod.Month && p.Year === newPeriod.Year);
                if (currentValue) {
                    newPeriod.ExcludedDays = currentValue.ExcludedDays ?? 0;
                    newPeriod.IncludableIncomeAmount = currentValue.IncludableIncomeAmount ?? 0;
                    if (currentValue.Id) {
                        newPeriod.Id = currentValue.Id;
                    } else if (currentValue[BindingContext.NEW_ENTITY_ID_PROP]) {
                        newPeriod[BindingContext.NEW_ENTITY_ID_PROP] = currentValue[BindingContext.NEW_ENTITY_ID_PROP];
                    }
                }
                if (!newPeriod.Id && !newPeriod[BindingContext.NEW_ENTITY_ID_PROP]) {
                    // current value could be defined, but without Id -> generated on BE (passed as default data), but without existing Id
                    newPeriod[BindingContext.NEW_ENTITY_ID_PROP] = ++newItemMax;
                }
                periods.push(newPeriod);
                from = from.add(1, "month");
            }
            periods.push(this.summaryItem());
            periods = sortPeriodsAndCreateKey(periods);
        }
        storage.setValueByPath(createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.DecisivePeriods), periods);
        if (periods?.length) {
            this.recalculateTotals();
        }
    }

    recalculateTotals(): void {
        const { storage } = this.props;
        const bc = storage.data.bindingContext
            .navigate(PrCSSZSubmissionEntity.Detail)
            .navigate(PrCSSZSubmissionDetailEntity.DecisivePeriods);
        const periods = storage.getValue(bc) as IPrCSSZDecisivePeriodEntity[];
        let totalDays = 0;
        let totalAmount = 0;
        if (periods && periods.length > 0) {
            periods.forEach(p => {
                const rowBc = bc.addKey(p);
                if (rowBc.getKey() === SummaryRowId ||
                    storage.hasError(rowBc.navigate(PrCSSZDecisivePeriodEntity.ExcludedDays)) ||
                    storage.hasError(rowBc.navigate(PrCSSZDecisivePeriodEntity.IncludableIncomeAmount))) {
                    return;
                }
                totalDays += NumberType.isValid(p.ExcludedDays) ? p.ExcludedDays : 0;
                totalAmount += NumberType.isValid(p.IncludableIncomeAmount) ? p.IncludableIncomeAmount : 0;
            });
        }
        const summaryBc = bc.addKey(SummaryRowId);
        storage.setValue(summaryBc.navigate(PrCSSZDecisivePeriodEntity.ExcludedDays), totalDays);
        storage.setValue(summaryBc.navigate(PrCSSZDecisivePeriodEntity.IncludableIncomeAmount), totalAmount);
    }

    handlePaymentMethodChange= (e: ISmartFieldChange): void => {
        const { storage } = this.props;
        if (e.bindingContext.getPath() === PrCSSZSubmissionDetailEntity.PaymentMethod && e.triggerAdditionalTasks) {
            const method = e.value;
            const detailBc = storage.data.bindingContext
                .navigate(PrCSSZSubmissionEntity.Detail);
            const entities = [
                { value: PrCSSZPaymentMethodCode.PaymentToAddress, property: PrCSSZSubmissionDetailEntity.PaymentConnectionDelivery },
                { value: PrCSSZPaymentMethodCode.PaymentToAccountAbroad, property: PrCSSZSubmissionDetailEntity.PaymentConnectionWireTransferAbroad },
                { value: PrCSSZPaymentMethodCode.PaymentToAccountInTheCzechRepublic, property: PrCSSZSubmissionDetailEntity.PaymentConnectionWireTransferCZ },
            ];
            entities.forEach(entity => {
                const bc = detailBc.navigate(entity.property);
                if (method === entity.value) {
                    storage.setValue(bc, {});
                } else {
                    storage.clearValue(bc);
                }
            });
        }
    }

    handleIncomeTypeChange = (e: ISmartFieldChange): void => {
        if (e.bindingContext.getPath() === IncomeTypeLocalPath && e.triggerAdditionalTasks) {
            this.updateDecisivePeriods();
        }
    };

    handleEmployeeChange = async (e: ISmartFieldChange): Promise<void> => {
        // todo: better check if employee select
        if (e.bindingContext.getPath() === PrCSSZSubmissionEntity.Employee && e.triggerAdditionalTasks) {
            await this.loadEmployments();

            // default payment method to account
            if (!isObjectEmpty(e.additionalData.BankAccount)) {
                const detailBc = this.props.storage.data.bindingContext.navigate(PrCSSZSubmissionEntity.Detail);
                const account = e.additionalData.BankAccount as IPrEmployeeBankAccountEntity;
                const isCZAccount = account.CountryCode === CountryCode.CzechRepublic;
                this.props.storage.setValue(detailBc.navigate(PrCSSZSubmissionDetailEntity.PaymentMethod),
                    isCZAccount ? PrCSSZPaymentMethodCode.PaymentToAccountInTheCzechRepublic : PrCSSZPaymentMethodCode.PaymentToAccountAbroad);

                const accountBc = detailBc.navigate(
                    isCZAccount ? PrCSSZSubmissionDetailEntity.PaymentConnectionWireTransferCZ : PrCSSZSubmissionDetailEntity.PaymentConnectionWireTransferAbroad
                );
                this.props.storage.setValue(accountBc, {
                    AccountNumber: account.AccountNumber,
                    AbaNumber: account.AbaNumber,
                    IBAN: account.IBAN,
                    SWIFT: account.SWIFT,
                    ...getExpandedEnumProps(EntityTypeName.Bank, account.BankCode, EntityBankAccountEntity.Bank),
                    ...getExpandedEnumProps(EntityTypeName.Country, account.CountryCode, EntityBankAccountEntity.Country)
                });
                this.props.storage.addActiveGroupByKey(PrSubmissionGroup.PaymentMethod);
            }
        }
    };

    handleDecisivePeriodMasterFieldsChange = (e: ISmartFieldChange): void => {
        const { storage } = this.props;
        const path = e.bindingContext.getPath();
        const parentBc = e.bindingContext.getParent();
        const parentPath = parentBc.getPath();

        const isEmploymentChange = path === PrCSSZSubmissionDetailEntity.Employment;
        const isEmploymentDateStartChange = parentPath === PrCSSZSubmissionDetailEntity.Employment && path === PrCSSZSubmissionEmploymentEntity.DateStart;
        const isEventStartChange = parentPath === PrCSSZSubmissionEntity.Detail && path === PrCSSZSubmissionEmploymentEntity.DateStart;
        if ((isEmploymentChange || isEmploymentDateStartChange || isEventStartChange) && e.triggerAdditionalTasks) {
            const detailBc = storage.data.bindingContext.navigate(PrCSSZSubmissionEntity.Detail);
            const employmentStartBc = detailBc
                .navigate(PrCSSZSubmissionDetailEntity.Employment)
                .navigate(PrCSSZSubmissionEmploymentEntity.DateStart);
            const dateEmploymentStart = storage.getValue(employmentStartBc);
            const hasEventStart = detailBc.isValidNavigation("DateStart");
            const eventStart = getUtcDayjs(hasEventStart ? storage.getValue(detailBc.navigate("DateStart")) : null);
            const periodStartBc = detailBc.navigate(PrCSSZSubmissionDetailEntity.DateDecisivePeriodStart);
            let periodStart = eventStart.subtract(1, "year").startOf("month");
            if (dateEmploymentStart && getUtcDayjs(periodStart).isBefore(dateEmploymentStart)) {
                periodStart = getUtcDayjs(dateEmploymentStart).startOf("month");
            }
            let hasChanged = !periodStart.isSame(storage.getValue(periodStartBc), "day");
            const periodEndBc = detailBc.navigate(PrCSSZSubmissionDetailEntity.DateDecisivePeriodEnd);
            let periodEnd = eventStart.subtract(1, "month").endOf("month");
            if (getUtcDayjs(periodEnd).isBefore(periodStart)) {
                periodEnd = periodStart.endOf("month");
            }
            hasChanged = hasChanged || !periodEnd.isSame(storage.getValue(periodEndBc), "day");
            if (hasChanged) {
                storage.setValue(periodStartBc, periodStart);
                storage.setValue(periodEndBc, periodEnd);
                this.updateDecisivePeriods();
                // show changes immediately
                storage.refresh(true);
            }
        }
    }

    handleDecisiveDateChange = (e: ISmartFieldChange): void => {
        const path = e.bindingContext.getPath();
        if ((path === PrCSSZSubmissionDetailEntity.DateDecisivePeriodStart || path === PrCSSZSubmissionDetailEntity.DateDecisivePeriodEnd)
            && e.triggerAdditionalTasks) {
            // generate periods for the selected range
            this.updateDecisivePeriods();
        }
    };

    handleEmployeeIdentificationTypeChange = (e: ISmartFieldChange): void => {
        const { storage } = this.props;
        const path = e.bindingContext.getPath();
        if (path === IdentificationTypeLocalPath && e.triggerAdditionalTasks) {
            // refresh group to recalculate grouping of fields
            storage.addActiveGroupByKey(PrSubmissionGroup.Employee);
        }
    };

    handleChange(e: ISmartFieldChange): void {
        this.handlePaymentMethodChange(e);

        super.handleChange(e);
        // keep after storage -> handleChange to have updated data
        this.handleIncomeTypeChange(e);
        this.handleDecisiveDateChange(e);
        this.handleDecisivePeriodMasterFieldsChange(e);
        this.handleEmployeeChange(e);
        this.handleEmployeeIdentificationTypeChange(e);

        const shouldUpdate = e.triggerAdditionalTasks !== false;
        this.props.storage.refreshFields(shouldUpdate);
    }

    handleLineItemsChange(e: ISmartFieldChange): void {

        if (e.bindingContext.getPath() === IdentificationTypeLocalPath) {
            this.props.storage.refresh(true);
        }

        super.handleLineItemsChange(e);
    }

    async handleBlur(args: ISmartFieldBlur): Promise<void> {
        const storage = this.props.storage as unknown as FormStorage;
        const path = args.bindingContext.getPath();

        if (args.wasChanged) {
            if ((path === PrCSSZDecisivePeriodEntity.ExcludedDays || path === PrCSSZDecisivePeriodEntity.IncludableIncomeAmount)) {
                this.recalculateTotals();
            }

            handleBankAccountOrIBANBlurWithAccountGeneration(getBankBlurArgs(storage, args.bindingContext));
            storage.addActiveGroupByKey(PrSubmissionGroup.IncomeInPeriod);
        }
        return super.handleBlur(args);
    }

    onBeforeSave(data?: E): E {
        const { storage } = this.props;

        data = super.onBeforeSave(data);

        if (storage.data.bindingContext.navigate(PrCSSZSubmissionEntity.Detail).isValidNavigation(PrCSSZSubmissionDetailEntity.Employment) && !data.Detail.Employment) {
            // Employment is nullable, it's not automatically created in entity, but has to exist for validation of subfields
            storage.data.entity.Detail.Employment = {};
        }

        if (!data.Detail) {
            data.Detail = {};
        }

        const employeeBc = storage.data.bindingContext
            .navigate(PrCSSZSubmissionEntity.Employee);
        handleIdentificationTypeBeforeSave(data, storage.data.bindingContext, employeeBc, {
            identificationPropName: PrEmployeeEntity.PersonalIdentificationNumber,
            useEuSocialInsuranceNumber: true
        });

        // remove summaryItem from decisive periods
        const periodsToSave: IPrCSSZDecisivePeriodEntity[] = [];
        data.Detail.DecisivePeriods?.forEach(item => {
            if (item.Id !== SummaryRowId) {
                periodsToSave.push(item);
            } else {
                data.Detail.EstimatedIncomeAmount = item.IncludableIncomeAmount;
                data.Detail.ExcludedDays = item.ExcludedDays;
            }
        });
        data.Detail.DecisivePeriods = periodsToSave;

        if (!isVisibleByPath(storage, createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.PaymentMethod))) {
            data.Detail.PaymentMethod = data.Detail.PaymentMethodCode = null;
        }
        // clear invisible objects
        if (data.Detail.PaymentMethodCode !== PrCSSZPaymentMethodCode.PaymentToAccountAbroad) {
            storage.data.entity.Detail.PaymentConnectionWireTransferAbroad = data.Detail.PaymentConnectionWireTransferAbroad = null;
        }
        if (data.Detail.PaymentMethodCode !== PrCSSZPaymentMethodCode.PaymentToAccountInTheCzechRepublic) {
            storage.data.entity.Detail.PaymentConnectionWireTransferCZ = data.Detail.PaymentConnectionWireTransferCZ = null;
        }
        if (data.Detail.PaymentMethodCode !== PrCSSZPaymentMethodCode.PaymentToAddress) {
            storage.data.entity.Detail.PaymentConnectionDelivery = data.Detail.PaymentConnectionDelivery = null;
        }

        return data;
    }
}
